import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, X, RotateCcw, User, Clock, Headset, FileText, Bell, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { enableAdminPush, disableAdminPush, isAdminPushActive } from "@/lib/adminPush";
import AdminPushNudge from "@/components/AdminPushNudge";

const STATUS_COLORS = {
  submitted: "bg-sky-100 text-sky-700",
  under_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  paid: "bg-violet-100 text-violet-700",
  duplicate: "bg-purple-100 text-purple-700",
};

function fmtRelative(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AdminLiveChatPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    isAdminPushActive().then(setPushEnabled).catch(() => setPushEnabled(false));
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await disableAdminPush();
        setPushEnabled(false);
        toast.success("Phone notifications turned off");
      } else {
        await enableAdminPush();
        setPushEnabled(true);
        toast.success("You'll now get a phone ping when chats arrive 🔔");
      }
    } catch (e) {
      toast.error(e?.message || "Could not toggle notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const sendTestPush = async () => {
    try {
      const r = await api.post("/admin/push/test");
      toast.success(`Test push sent to ${r.data.sent_to_devices} device(s)`);
    } catch {
      toast.error("Test push failed — make sure notifications are enabled.");
    }
  };

  // Load sessions list, refresh every 5s
  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/admin/live-chat/sessions");
      setSessions(r.data || []);
      if (!selectedId && (r.data || []).length > 0) {
        setSelectedId(r.data[0].id);
      }
    } catch {
      toast.error("Couldn't load chats");
    }
  }, [selectedId]);

  useEffect(() => {
    loadSessions();
    const id = setInterval(loadSessions, 5000);
    return () => clearInterval(id);
  }, [loadSessions]);

  // Load selected session detail + poll every 3s
  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      const r = await api.get(`/admin/live-chat/${selectedId}`);
      setDetail(r.data);
    } catch {
      /* silent */
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return undefined;
    }
    loadDetail();
    const id = setInterval(loadDetail, 3000);
    return () => clearInterval(id);
  }, [selectedId, loadDetail]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/admin/live-chat/${selectedId}/message`, { text });
      setDraft("");
      loadDetail();
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const claim = async () => {
    try {
      await api.post(`/admin/live-chat/${selectedId}/claim`);
      toast.success("You're now handling this chat");
      loadDetail();
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim");
    }
  };

  const release = async () => {
    if (!confirm("Hand this conversation back to EVA?")) return;
    try {
      await api.post(`/admin/live-chat/${selectedId}/release`);
      toast.success("Released to EVA");
      loadDetail();
      loadSessions();
    } catch {
      toast.error("Could not release");
    }
  };

  const close = async () => {
    if (!confirm("Close this conversation? The customer can still start a new chat.")) return;
    try {
      await api.post(`/admin/live-chat/${selectedId}/close`);
      toast.success("Conversation closed");
      loadDetail();
      loadSessions();
    } catch {
      toast.error("Could not close");
    }
  };

  const session = detail?.session;
  const messages = detail?.messages || [];
  const customerSubs = detail?.submissions || [];

  return (
    <div className="px-2 sm:px-4 md:px-6 mt-2" data-testid="admin-live-chat-page">
      <AdminPushNudge onEnabled={() => setPushEnabled(true)} />
      {/* Page header with push-notification controls */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Live Chat</h1>
          <p className="text-xs text-slate-500">Real-time customer conversations · auto-handover from EVA</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePush}
            disabled={pushBusy}
            data-testid="live-chat-toggle-push"
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
              pushEnabled
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
            title={pushEnabled ? "Phone notifications ON" : "Enable phone notifications"}
          >
            {pushEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            {pushEnabled ? "Phone alerts ON" : "Enable phone alerts"}
          </button>
          {pushEnabled && (
            <button
              onClick={sendTestPush}
              data-testid="live-chat-test-push"
              className="h-9 px-3 rounded-lg text-xs font-semibold bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            >
              Send test
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr,320px] min-h-[calc(100vh-180px)]">
          {/* ────────────────────────── COLUMN 1: INBOX ────────────────────────── */}
          <aside
            className="border-r border-slate-100 bg-slate-50/50"
            data-testid="live-chat-inbox"
          >
            <div className="px-4 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <Headset className="w-4 h-4 text-rose-500" />
                Inbox
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {sessions.filter((s) => !s.closed_at).length} open · {sessions.length} total
              </p>
            </div>
            <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
              {sessions.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-slate-400">
                  No chats yet. They'll appear here in real time.
                </div>
              )}
              {sessions.map((s) => {
                const active = selectedId === s.id;
                const isClosed = !!s.closed_at;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    data-testid={`live-chat-session-${s.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-white transition-colors ${
                      active ? "bg-white border-l-4 border-l-rose-500" : ""
                    } ${isClosed ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {s.customer_name || s.customer_email || "Guest visitor"}
                      </div>
                      <div className="text-[10px] text-slate-400 flex-shrink-0">
                        {fmtRelative(s.last_message_at)}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.last_message_preview || "No messages yet"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {s.manager_requested && !isClosed && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-500 text-white animate-pulse">
                          MANAGER REQUESTED
                        </span>
                      )}
                      {(s.unread_count || 0) > 0 && !isClosed && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-sky-500 text-white">
                          {s.unread_count}
                        </span>
                      )}
                      {s.claimed_by && !isClosed && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-violet-50 text-violet-700">
                          ON IT
                        </span>
                      )}
                      {isClosed && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-200 text-slate-600">
                          CLOSED
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ─────────────────────── COLUMN 2: CONVERSATION ─────────────────────── */}
          <section className="flex flex-col bg-white" data-testid="live-chat-conversation">
            {!session && (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
                Select a conversation from the inbox to start.
              </div>
            )}
            {session && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {session.customer_name || session.customer_email || "Guest visitor"}
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Started {fmtRelative(session.created_at)}
                      {session.claimed_by && <> · handled by {session.claimed_by}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!session.claimed_by && !session.closed_at && (
                      <button
                        onClick={claim}
                        data-testid="live-chat-claim"
                        className="h-9 px-3 rounded-lg text-xs font-semibold bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white hover:from-rose-600 hover:to-fuchsia-600"
                      >
                        Take this chat
                      </button>
                    )}
                    {session.claimed_by && !session.closed_at && (
                      <>
                        <button
                          onClick={release}
                          data-testid="live-chat-release"
                          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Hand back to EVA
                        </button>
                        <button
                          onClick={close}
                          data-testid="live-chat-close"
                          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 text-rose-600"
                        >
                          <X className="w-3.5 h-3.5" /> Close
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/40 max-h-[calc(100vh-340px)]">
                  {messages.map((m) => {
                    const isCustomer = m.role === "user";
                    const isManager = m.role === "manager";
                    const isSystem = m.system === true;
                    return (
                      <div
                        key={m.id}
                        className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                            isSystem
                              ? "bg-amber-50 text-amber-800 text-xs border border-amber-200"
                              : isCustomer
                              ? "bg-white border border-slate-200 text-slate-900"
                              : isManager
                              ? "bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white"
                              : "bg-sky-500 text-white"
                          }`}
                        >
                          {!isSystem && !isCustomer && (
                            <div className="text-[10px] font-semibold opacity-80 mb-0.5">
                              {isManager ? (m.manager_name || "Manager") : "EVA (bot)"}
                            </div>
                          )}
                          {m.text || (m.image_key ? "📎 Image attached" : "")}
                          <div className={`text-[9px] mt-1 opacity-60 ${isCustomer ? "text-slate-500" : "text-white/80"}`}>
                            {fmtRelative(m.timestamp)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Composer */}
                {!session.closed_at && (
                  <div className="border-t border-slate-100 px-4 py-3 flex items-end gap-2 bg-white">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type your reply… (Shift+Enter for new line)"
                      rows={2}
                      data-testid="live-chat-composer"
                      className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={sending || !draft.trim()}
                      data-testid="live-chat-send"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-sm font-bold bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white shadow disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" /> Send
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* ─────────────────────── COLUMN 3: CONTEXT ─────────────────────── */}
          <aside
            className="border-l border-slate-100 bg-slate-50/50"
            data-testid="live-chat-context"
          >
            {!session && (
              <div className="px-4 py-10 text-center text-sm text-slate-400">
                Customer context appears here
              </div>
            )}
            {session && (
              <div className="px-4 py-4">
                <div className="font-bold text-slate-900 flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-slate-600" /> Customer
                </div>
                <div className="space-y-1.5 mb-5 text-sm">
                  <Field label="Name" value={session.customer_name || "(guest)"} />
                  <Field label="Email" value={session.customer_email || "—"} mono />
                  <Field label="Session" value={session.id.slice(0, 8) + "…"} mono />
                  <Field label="IP" value={session.ip || "—"} mono />
                </div>

                <div className="font-bold text-slate-900 flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-slate-600" /> Submissions ({customerSubs.length})
                </div>
                {customerSubs.length === 0 && (
                  <div className="text-xs text-slate-400 py-2">No submissions yet</div>
                )}
                <div className="space-y-2 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
                  {customerSubs.map((s) => (
                    <a
                      key={s.id}
                      href={`/admin/submissions?q=${encodeURIComponent(s.reference)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block border border-slate-200 rounded-xl px-3 py-2 bg-white hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-[11px] font-mono font-bold text-slate-900">
                          {s.reference}
                        </code>
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            STATUS_COLORS[s.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {(s.status || "—").toUpperCase()}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">
                        {(s.cards || [])
                          .map((c) => `${c.brand_name} $${Number(c.balance).toFixed(0)}`)
                          .join(" · ") || "—"}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-between">
                        <span>{fmtRelative(s.created_at)}</span>
                        <span className="font-mono text-emerald-700 font-semibold">
                          ${Number(s.estimated_payout || 0).toFixed(2)}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold w-16 flex-shrink-0">
        {label}
      </span>
      <span className={`text-xs text-slate-900 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
